import _ from 'lodash'
import commonLocaleBundle from './'
import ConfigurationService from '../config/configService'

/**
 * Configuration namespace for this module
 * @type {string}
 */
const moduleConfNamespace = 'locale'

/**
 * Locale bundles
 * Sorted by ascending order of precedence
 */
const loadedLocaleBundles = [
  { common: commonLocaleBundle },
  { client: false }
]

/**
 * Other dependencies used by this service
 * @type {{conf: {get: (function(*=, *=): *), replaceLoadedBundle: ConfigService.replaceLoadedBundle, getLoadedBundles: (function(): [{common: {}}, {client: boolean}])}}}
 */
const dependencies = {
  conf: ConfigurationService
}

/**
 * Gets the requested localization item (by its key & namespace)
 * @param key
 * @param params
 * @param namespace
 * @param targetLocale
 * @returns {*}
 */
const getTranslation = (key, { params, namespace, targetLocale = false }) => {
  const selectedLocale = (targetLocale === false) ? discoverCurrentLocale() : targetLocale
  let localizationValue = null
  _.forEach(_.clone(loadedLocaleBundles).reverse(), (localization) => {
    const localeBundleData = Object.values(localization)[0]
    if (localizationValue !== null) { return localizationValue }
    const localizationValueInBundle = localizationResolution(localeBundleData[selectedLocale], key, namespace)
    if (_.isUndefined(localizationValueInBundle)) { return }
    if (_.isNull(localizationValueInBundle) && !_.isNull(localizationValue)) { return }
    localizationValue = localizationValueInBundle
  })
  if (_.isEmpty(localizationValue) && targetLocale === false) {
    return getTranslation(key, {params: params, namespace: namespace, targetLocale: getLocaleFallback() })
  }
  const finalLocalizationValue = (_.isEmpty(localizationValue)) ? key : localizationValue
  return parseLocaleValue(finalLocalizationValue, params)
}

/**
 * Gets the fallback locale from config
 * @returns {*}
 */
const getLocaleFallback = () => {
  return dependencies.conf.get('localeFallback', moduleConfNamespace)
}

/**
 * Discovers what the current locale is
 * @returns {*}
 */
const discoverCurrentLocale = () => {
  const configuredStrategyName = dependencies.conf.get('localePickingStrategy.method', moduleConfNamespace)
  const strategy = _.get(localizationPickingStrategies, configuredStrategyName)
  const discoveredLocale = strategy(this)
  if (!_.includes(dependencies.conf.get('availableLocales', moduleConfNamespace), discoveredLocale)) {
    return getLocaleFallback()
  }
  return discoveredLocale
}

/**
 * Parses localization values
 * @param localizationValue
 * @param params
 * @returns {any}
 */
const parseLocaleValue = (localizationValue, params) => {
  // Fetch all nameless params in a separate array
  const namelessParams = _.filter(params, param => !_.isObject(param))
  // Gets the next nameless param from namelessParams
  const getNextNamelessParamValue = () => {
    if (typeof getNextNamelessParamValue.namelessParamIdx === 'undefined') { getNextNamelessParamValue.namelessParamIdx = -1 }
    getNextNamelessParamValue.namelessParamIdx++
    return _.get(namelessParams, getNextNamelessParamValue.namelessParamIdx)
  }
  // Fetch all named params in a separate key value object and join them together (if multiple named params are found)
  const namedParams = _.reduce(
    _.filter(params, param => _.isObject(param)),
    (namedParamAcc, param, paramIdx) => { return { ...namedParamAcc, ...param } }
  )
  /*
  * Getter that takes in the full localization value part*,
  * extracts the name of the named pattern and attempts to return the provided value for that name
  * if localizationValuePart contained pattern is nameless returns false
  * if localizationValuePart contained pattern is named, returns the value from namedParams
  *   If there isn't such value in namedParams array, returns undefined. (Lo's default _.get behaviour)
  * *localization value part has to include zero or one occurances of either named or nameless pattern
  *   See localization value splittage (positive lookahead) below of how it is achieved
  */
  const getNamedParamValue = (localizationValuePart) => {
    const pattern = _.last(localizationValuePart.match(/{{@\w*}}/g))
    const namedParamFromPattern = _.replace(pattern, /^{{@|}}$/gm, '')
    const namedParamValue = _.get(namedParams, namedParamFromPattern)
    if (_.isUndefined(namedParamValue) && _.eq(namedParamFromPattern, '')) { return false }
    return namedParamValue
  }
  /**
   * Returns false when localizationvaluePart doesn't contain any patterns
   * @param localizationValuePart
   * @returns {*}
   */
  const doesntValuePartContainPattern = (localizationValuePart) => {
    return _.isEmpty(localizationValuePart.match(/{{@\w*}}/g))
  }
  /**
   * Splits localization value to sections
   * that contain either 0 or 1 occurrences of patterns
   * For example given the string "My dog {{@dogName}} likes to play with {{@}}" will be split as following:
   * ["My dog ", "{{@dogName}} likes to play with ", "{{@}}"]
   * Positive lookahead, because we need to keep the patterns in the split,
   * as they'll be replaced with the matching value from the provided parameters
   * !!Or replaced with empty string if not found in provided parameters!!
   */
  const localizationValueSplit = _.split(localizationValue, /(?={{@\w*}})/g)
  const replacedLocalizationValueSplit = _.map(localizationValueSplit, (localizationValuePart, idx) => {
    // Return parts without any pattern occurances as is
    if (doesntValuePartContainPattern(localizationValuePart)) { return localizationValuePart }
    const namedParamValue = getNamedParamValue(localizationValuePart)
    // Replace named patterns, with empty string, if those patterns are nowhere to be found in provided params
    if (_.isUndefined(namedParamValue)) { return _.replace(localizationValuePart, /{{@\w*}}/g, '') }
    // Handling of nameless patterns
    if (namedParamValue === false) {
      const namelessParamValue = getNextNamelessParamValue()
      // Have we gone too far? Are there no more nameless patterns to fetch? replace with empty str if that's the case
      if (_.isUndefined(namelessParamValue)) { return _.replace(localizationValuePart, /{{@\w*}}/g, '') }
      return _.replace(localizationValuePart, /{{@\w*}}/g, namelessParamValue)
    }
    return _.replace(localizationValuePart, /{{@\w*}}/g, namedParamValue)
  })
  // Put the split localization Value Parts back together
  return _.join(replacedLocalizationValueSplit, '')
}

/**
 * List of strategies to be used in finding the current locale
 * @type {{discoverLocaleFromUrl: (function(): (boolean|*)), getLocaleFromConfig: (function({locale: *}): *)}}
 */
const localizationPickingStrategies = {
  discoverLocaleFromUrl: () => {
    const targetLevel = dependencies.conf.get('localePickingStrategy.targetDomainLevel', moduleConfNamespace)
    const locationHostChunked = window.location.host.split('.')
    const locationChunkLengthDiff = locationHostChunked.length - targetLevel
    if (locationChunkLengthDiff < 0) { return false }
    return _.get(locationHostChunked, locationChunkLengthDiff)
  },
  getLocaleFromConfig: () => {
    return dependencies.conf.get('localePickingStrategy.locale', moduleConfNamespace)
  }
}


/**
 * Given a list of localizations, it will attempt to retrieve a value
 * from a given key [and optionally a given namespace]
 * @param localization
 * @param keySetting
 * @param keyNamespace
 * @returns {null|*}
 */
const localizationResolution = (localization, keySetting, keyNamespace = null) => {
  if (keyNamespace !== null) {
    if (_.has(localization, [keyNamespace, keySetting])) {
      return _.get(localization, [keyNamespace, keySetting])
    }
    return null
  }
  const foundLocaleSettings = _.find(localization, keySetting)
  if (typeof foundLocaleSettings === 'undefined') {
    return null
  }
  return foundLocaleSettings[keySetting]
}

const LocaleService = {
  /**
   * Gets the locale item
   * @param key
   * @param options
   * @returns {any}
   */
  translate: (key, options) => {
    const compiledOptions = _.defaults(options || {}, {
      namespace: dependencies.conf.get('defaultNamespaceLookup', moduleConfNamespace),
      params: []
    })
    return getTranslation(key, { params: compiledOptions.params, namespace: compiledOptions.namespace })
  },
  replaceLoadedBundle: (bundleName, payload) => {
    const bundleIdx = _.findIndex(loadedLocaleBundles, (bundle) => _.get(_.keys(bundle), 0) === bundleName)
    loadedLocaleBundles[bundleIdx] = { [bundleName]: payload }
  },
  replaceDependency: (dependencyName, payload) => {
    dependencies[dependencyName] = payload
  },
  getLoadedBundles: () => {
    return loadedLocaleBundles
  },
  getCurrentLocale: () => {
    return discoverCurrentLocale()
  }
}

export default LocaleService
